import { Bank, CreditCard, Percent, ShieldCheck, Wallet } from 'phosphor-react';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Buyer,
  getEffectiveCreditCardFeeWaiverConditions,
  getOrdinalSuffix,
  hasCurrentDueWaiverCutoff,
  NO_CURRENT_DUE_WAIVER_CUTOFF,
} from '@dill/dill-shared';
import { twMerge } from 'tailwind-merge';
import { COLORS } from '../../../../../utils/colors';
import { AppRadioButton } from '../../../../general/AppRadioButton/AppRadioButton';
import { DillButton, DillCard, DillModal, DillSelect, DillToggle } from '../../../../general/Dill';
import { useAppSelector, AppDispatch } from '../../../../../redux/store';
import AppRangeSlider from '../../../../general/AppAppRangeSlider/AppRangeSlider';
import {
  updateSupplierBuyerFiservFeeRate,
  updateSupplierBuyer,
} from '../../../../../redux/services/supplierBuyersService';
import { openDillToast } from '../../../../../utils/helpers';

type FeePayer = 'SUPPLIER' | 'BUYER' | 'BOTH';
type PaymentType = 'CREDIT_CARD' | 'DEBIT_CARD' | 'BANK';

/** Prox terms most commonly land on the 10th, so that is what the toggle starts at. */
const DEFAULT_WAIVER_DAY_OF_MONTH = 10;

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  label: getOrdinalSuffix(i + 1),
  value: `${i + 1}`,
}));

const PAYMENT_TYPE_META: Record<
  PaymentType,
  { label: string; description: string; Icon: typeof CreditCard }
> = {
  CREDIT_CARD: {
    label: 'Credit Card',
    description: 'Disable credit card payments for this customer',
    Icon: CreditCard,
  },
  DEBIT_CARD: {
    label: 'Debit Card',
    description: 'Disable debit card payments for this customer',
    Icon: Wallet,
  },
  BANK: {
    label: 'Bank Transfer',
    description: 'Disable bank transfer payments for this customer',
    Icon: Bank,
  },
};

const SectionHeader = ({
  icon,
  iconBg,
  title,
  description,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description: string;
}) => (
  <div className="flex items-start gap-3">
    <div
      className={twMerge('flex h-8 w-8 shrink-0 items-center justify-center rounded-md', iconBg)}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
    </div>
  </div>
);

const FeePayerOption = ({
  label,
  helper,
  isChecked,
  isDisabled,
  onClick,
}: {
  label: string;
  helper: string;
  isChecked: boolean;
  isDisabled: boolean;
  onClick: () => void;
}) => (
  <div
    role="radio"
    aria-checked={isChecked}
    aria-disabled={isDisabled}
    tabIndex={isDisabled ? -1 : 0}
    onClick={() => {
      if (!isDisabled) onClick();
    }}
    onKeyDown={(e) => {
      if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onClick();
      }
    }}
    className={twMerge(
      'flex items-center gap-3 rounded-lg border bg-white px-4 py-3 transition-colors',
      isDisabled
        ? 'cursor-not-allowed border-gray-200 opacity-70'
        : 'cursor-pointer border-gray-200 hover:border-gray-300',
      isChecked && !isDisabled && 'border-blue-500 bg-blue-50 hover:border-blue-500'
    )}>
    <AppRadioButton isChecked={isChecked} isDisabled={isDisabled} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <p className="text-xs text-gray-500 mt-0.5">{helper}</p>
    </div>
  </div>
);

const SettingRow = ({
  icon,
  title,
  description,
  value,
  onChange,
  isDisabled = false,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
  isDisabled?: boolean;
  children?: React.ReactNode;
}) => (
  <div
    className={twMerge(
      'flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3',
      isDisabled && 'opacity-50'
    )}>
    <div className="flex items-start gap-3 flex-1 min-w-0">
      {icon && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100">
          {icon}
        </div>
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        {value && children}
      </div>
    </div>
    <div className="pt-0.5">
      <DillToggle value={value} onChange={onChange} isDisabled={isDisabled} />
    </div>
  </div>
);

const CreditCardSettingsModal = ({
  open,
  selectedCustomer,
  handleClose,
}: {
  open: boolean;
  selectedCustomer: Buyer | null;
  handleClose: (isUpdated?: boolean) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
  const isFiserv = !isProduction || currentSupplier?.cardPaymentProvider === 'FISERV';
  const maxFeeRate = currentSupplier?.creditCardTakeRate || 3.5;

  // Fee settings state
  const [localFeeRate, setLocalFeeRate] = useState<number>(0);
  const [initialFeeRate, setInitialFeeRate] = useState<number>(0);

  // Waiver settings state
  const [localWaiveForCurrentDuePayments, setLocalWaiveForCurrentDuePayments] = useState(false);
  const [initialWaiveForCurrentDuePayments, setInitialWaiveForCurrentDuePayments] = useState(false);
  const [localWaiveForDiscountDatePayments, setLocalWaiveForDiscountDatePayments] = useState(false);
  const [initialWaiveForDiscountDatePayments, setInitialWaiveForDiscountDatePayments] =
    useState(false);
  // null means off; a number is both the on-switch and the cutoff day.
  const [localWaiveDayOfMonth, setLocalWaiveDayOfMonth] = useState<number | null>(null);
  const [initialWaiveDayOfMonth, setInitialWaiveDayOfMonth] = useState<number | null>(null);

  // Disabled payment types state
  const [localDisabledPaymentTypes, setLocalDisabledPaymentTypes] = useState<PaymentType[]>([]);
  const [initialDisabledPaymentTypes, setInitialDisabledPaymentTypes] = useState<PaymentType[]>([]);

  const [isSaving, setIsSaving] = useState(false);

  // Check if fee settings have changed
  const hasFeeChanges = localFeeRate !== initialFeeRate;

  // Check if waiver settings have changed
  const hasWaiverChanges =
    localWaiveForCurrentDuePayments !== initialWaiveForCurrentDuePayments ||
    localWaiveForDiscountDatePayments !== initialWaiveForDiscountDatePayments ||
    localWaiveDayOfMonth !== initialWaiveDayOfMonth;

  // Check if payment types have changed
  const hasPaymentTypesChanges =
    JSON.stringify([...localDisabledPaymentTypes].sort()) !==
    JSON.stringify([...initialDisabledPaymentTypes].sort());

  const hasAnyChanges = hasFeeChanges || hasWaiverChanges || hasPaymentTypesChanges;

  // Initialize local state when modal opens or customer changes
  useEffect(() => {
    if (selectedCustomer) {
      const feeRate = isFiserv
        ? selectedCustomer?.fiservBuyerFeeRate || (isProduction ? 0 : maxFeeRate)
        : 0;
      setLocalFeeRate(feeRate);
      setInitialFeeRate(feeRate);

      // Flags the customer has not customized show the supplier-level default
      const {
        waiveForCurrentDuePayments,
        waiveForDiscountDatePayments,
        currentDueWaiverDayOfMonthCutoff,
      } = getEffectiveCreditCardFeeWaiverConditions({
        supplier: currentSupplier,
        buyer: selectedCustomer,
      });

      setLocalWaiveForCurrentDuePayments(waiveForCurrentDuePayments);
      setInitialWaiveForCurrentDuePayments(waiveForCurrentDuePayments);

      setLocalWaiveForDiscountDatePayments(waiveForDiscountDatePayments);
      setInitialWaiveForDiscountDatePayments(waiveForDiscountDatePayments);

      // Local state uses null for "no deadline"; it becomes the sentinel on save.
      const effectiveCutoff = hasCurrentDueWaiverCutoff(currentDueWaiverDayOfMonthCutoff)
        ? currentDueWaiverDayOfMonthCutoff
        : null;
      setLocalWaiveDayOfMonth(effectiveCutoff);
      setInitialWaiveDayOfMonth(effectiveCutoff);

      const disabledTypes = selectedCustomer?.disabledPaymentTypes || [];
      setLocalDisabledPaymentTypes(disabledTypes as PaymentType[]);
      setInitialDisabledPaymentTypes(disabledTypes as PaymentType[]);
    }
  }, [
    open,
    selectedCustomer,
    isFiserv,
    isProduction,
    maxFeeRate,
    currentSupplier?.creditCardFeeWaiverConditions,
  ]);

  const handleFeePayerChange = (payer: FeePayer) => {
    if (!isFiserv) return;

    if (payer === 'SUPPLIER') {
      setLocalFeeRate(0);
    } else {
      // Set to max if switching from supplier-paid to contractor-paid with 0 rate
      if (localFeeRate === 0) {
        setLocalFeeRate(maxFeeRate);
      }
    }
  };

  const handleSliderChange = ({ value }: { value: number; percentage: number }) => {
    if (isFiserv) {
      setLocalFeeRate(value);
    }
  };

  const togglePaymentType = (type: PaymentType) => {
    setLocalDisabledPaymentTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSaveAll = async () => {
    if (!selectedCustomer?.id || !currentSupplier?.id || !hasAnyChanges) {
      return;
    }

    const supplierId = currentSupplier.id;
    const buyerId = selectedCustomer.id;

    setIsSaving(true);
    try {
      const tasks: Promise<unknown>[] = [];

      // Fee rate (FISERV only) — separate endpoint
      if (hasFeeChanges && isFiserv) {
        tasks.push(
          dispatch(
            updateSupplierBuyerFiservFeeRate({
              supplierId,
              buyerId,
              fiservBuyerFeeRate: localFeeRate,
            })
          ).then((result) => {
            if (!updateSupplierBuyerFiservFeeRate.fulfilled.match(result)) {
              throw new Error('fee');
            }
          })
        );
      }

      // Waiver + disabled payment types share the same endpoint — batch into one call
      if (hasWaiverChanges || hasPaymentTypesChanges) {
        const supplierBuyerDetails: Record<string, unknown> = {};
        if (hasWaiverChanges) {
          supplierBuyerDetails.creditCardFeeWaiverConditions = {
            waiveForCurrentDuePayments: localWaiveForCurrentDuePayments,
            waiveForDiscountDatePayments: localWaiveForDiscountDatePayments,
            // Never null — that would read back as "inherit" and hand the customer the
            // supplier's deadline again, making the per-customer exemption unsettable.
            currentDueWaiverDayOfMonthCutoff: localWaiveDayOfMonth ?? NO_CURRENT_DUE_WAIVER_CUTOFF,
          };
        }
        if (hasPaymentTypesChanges) {
          supplierBuyerDetails.disabledPaymentTypes = localDisabledPaymentTypes;
        }

        tasks.push(
          dispatch(
            updateSupplierBuyer({
              supplierId,
              supplierBuyerId: buyerId,
              supplierBuyerDetails,
            })
          ).then((result) => {
            if (!updateSupplierBuyer.fulfilled.match(result)) {
              throw new Error('buyer');
            }
          })
        );
      }

      await Promise.all(tasks);

      // Commit initial values
      if (hasFeeChanges && isFiserv) setInitialFeeRate(localFeeRate);
      if (hasWaiverChanges) {
        setInitialWaiveForCurrentDuePayments(localWaiveForCurrentDuePayments);
        setInitialWaiveForDiscountDatePayments(localWaiveForDiscountDatePayments);
        setInitialWaiveDayOfMonth(localWaiveDayOfMonth);
      }
      if (hasPaymentTypesChanges) setInitialDisabledPaymentTypes([...localDisabledPaymentTypes]);

      openDillToast({
        message: 'Payment settings updated successfully',
        type: 'SUCCESS',
      });
      handleClose(true);
    } catch (error) {
      console.error('Failed to update payment settings:', error);
      openDillToast({ message: 'Failed to update payment settings', type: 'ERROR' });
    } finally {
      setIsSaving(false);
    }
  };

  const isSupplierSelected = isFiserv
    ? localFeeRate === 0
    : selectedCustomer?.whoPaysCreditCardFee === 'SUPPLIER';
  const isContractorSelected = isFiserv
    ? localFeeRate > 0
    : selectedCustomer?.whoPaysCreditCardFee === 'BOTH' ||
      selectedCustomer?.whoPaysCreditCardFee === 'BUYER';

  return (
    <DillModal
      open={open}
      onClose={() => {
        if (!isSaving) handleClose();
      }}
      title="Payment Settings"
      subtitle={selectedCustomer?.name || undefined}
      maxWidth="640px"
      contentClassName="flex-1 min-h-0 overflow-y-auto px-5 py-5 flex flex-col gap-4"
      footer={
        <>
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={() => handleClose()}
            isDisabled={isSaving}
          />
          <DillButton
            label="Save changes"
            variant="primary"
            onClick={handleSaveAll}
            isLoading={isSaving}
            isDisabled={isSaving || !hasAnyChanges}
          />
        </>
      }>
      {/* Fee Settings Section */}
      <DillCard className="bg-white">
        <SectionHeader
          icon={<Percent size={16} color={COLORS.PRIMARY_500} weight="bold" />}
          iconBg="bg-blue-50"
          title="Credit Card Fees"
          description="Set fees for credit card payments only. Debit card surcharging is not permitted by federal law."
        />

        <div className="mt-4 flex flex-col gap-2">
          <FeePayerOption
            label="Fees paid by supplier"
            helper="Absorb credit card fees on your end"
            isChecked={!!isSupplierSelected}
            isDisabled={!isFiserv}
            onClick={() => handleFeePayerChange('SUPPLIER')}
          />
          <FeePayerOption
            label="Fees paid by contractor"
            helper="Pass credit card fees to the customer"
            isChecked={!!isContractorSelected}
            isDisabled={!isFiserv}
            onClick={() => handleFeePayerChange('BUYER')}
          />

          {isFiserv && localFeeRate > 0 && (
            <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-4 pt-3 pb-1">
              <p className="text-xs font-medium text-gray-600 mb-1">Contractor fee rate</p>
              <AppRangeSlider
                key={`slider-${maxFeeRate}`}
                min={0}
                max={maxFeeRate}
                valueUnits="%"
                valueLabel=""
                otherValueLabel={''}
                value={localFeeRate}
                onChange={handleSliderChange}
              />
            </div>
          )}
        </div>
      </DillCard>

      {/* Waiver Settings Section — only relevant when contractor is paying a fee */}
      {localFeeRate > 0 && (
        <DillCard className="bg-white">
          <SectionHeader
            icon={<ShieldCheck size={16} color={COLORS.SUCCESS_700} weight="bold" />}
            iconBg="bg-green-50"
            title="Waive Fee Conditions"
            description="Configure when credit card fees should be waived for this customer."
          />

          <div className="mt-4 flex flex-col gap-2">
            <SettingRow
              title="Waive fees for current due invoices"
              description="Credit card fees will be waived for invoices that are not past due."
              value={localWaiveForCurrentDuePayments}
              onChange={(v) => setLocalWaiveForCurrentDuePayments(v)}>
              <div className="mt-3 border-t border-gray-100 pt-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800">
                      Only until a day of the month
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      For prox terms. The waiver is lost after this day, even on invoices that are
                      not yet past due.
                    </p>
                  </div>
                  <DillToggle
                    value={localWaiveDayOfMonth !== null}
                    onChange={(v) =>
                      setLocalWaiveDayOfMonth(v ? DEFAULT_WAIVER_DAY_OF_MONTH : null)
                    }
                  />
                </div>
                {localWaiveDayOfMonth !== null && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-600">Paid on or before the</span>
                    <div className="w-24">
                      <DillSelect
                        value={`${localWaiveDayOfMonth}`}
                        options={DAY_OF_MONTH_OPTIONS}
                        onChange={(value) => setLocalWaiveDayOfMonth(Number(value))}
                      />
                    </div>
                    <span className="text-xs text-gray-600">of the month</span>
                  </div>
                )}
              </div>
            </SettingRow>
            <SettingRow
              title="Waive fees for discount date invoices"
              description="Credit card fees will be waived for invoices that are within the discount date."
              value={localWaiveForDiscountDatePayments}
              onChange={(v) => setLocalWaiveForDiscountDatePayments(v)}
            />
          </div>
        </DillCard>
      )}

      {/* Payment Types Section */}
      <DillCard className="bg-white">
        <SectionHeader
          icon={<Wallet size={16} color={COLORS.WARNING_700} weight="bold" />}
          iconBg="bg-yellow-50"
          title="Payment Methods"
          description="Disable specific payment methods for this customer."
        />

        <div className="mt-4 flex flex-col gap-2">
          {(Object.keys(PAYMENT_TYPE_META) as PaymentType[]).map((type) => {
            const { label, description, Icon } = PAYMENT_TYPE_META[type];
            const isSupplierDebitDisabled =
              type === 'DEBIT_CARD' && !!currentSupplier?.isDebitCardDisabled;
            return (
              <SettingRow
                key={type}
                icon={<Icon size={16} color={COLORS.GREY_600} weight="bold" />}
                title={label}
                description={description}
                value={isSupplierDebitDisabled || localDisabledPaymentTypes.includes(type)}
                onChange={() => togglePaymentType(type)}
                isDisabled={isSupplierDebitDisabled}
              />
            );
          })}
        </div>
      </DillCard>
    </DillModal>
  );
};

export default CreditCardSettingsModal;
